// crewbit-admin/src/pages/Schedule/walkInHandlers.ts
//
// Schedule Module Phase C C5A — Day-canvas dispatcher handler extraction (GR-14).
// Owns the reassign + walk-in drop/override callbacks + pending-mutation state
// extracted from Schedule/index.tsx to keep that file under the 500-LoC cap.
//
// Caller passes shared state setters (setServerConflicts / setToast / refresh)
// — keeping a single <ConflictBanner> across reassign + walk-in flows. Hook
// returns the discriminated `dayCanvasConflictRpcName` so caller can wire the
// banner's namespace lookup correctly per pending mutation.

import { useCallback, useState } from 'react';
import { useAssignWalkIn } from '../../hooks/useAssignWalkIn';
import { useReassignScheduleEntry } from '../../hooks/useReassignScheduleEntry';
import { lookupErrorCopy } from '../../lib/errorMap';
import type { ConflictDetail } from '../../lib/errorMap';

export interface PendingWalkInAssign {
  walkInId: string;
  operatorId: string;
  startDate: string;
  startTime: string;
}

export interface PendingReassign {
  entryId: string;
  newAssigneeId: string;
}

export interface UseDayCanvasDispatcherArgs {
  refresh: () => void;
  setToast: (t: { msg: string; type: 'success' | 'error' }) => void;
}

export interface UseDayCanvasDispatcherResult {
  serverConflicts: ConflictDetail[] | null;
  conflictRpcName: 'assign_walk_in' | 'reassign_schedule_entry';
  /** Reassign drop (existing C1.6 path). */
  handleReassign: (droppedEntryId: string, newResourceId: string) => Promise<void>;
  /** Walk-in drop (C5A). Time defaults to "now" rounded to next 15-min slot. */
  handleWalkInDrop: (walkInId: string, operatorId: string, anchorDate: Date) => Promise<void>;
  /** Banner Override CTA — retries the pending mutation with p_force=true. */
  handleOverride: () => Promise<void>;
  /** Banner Dismiss CTA — clears all pending state. */
  handleDismiss: () => void;
}

export function useDayCanvasDispatcher(args: UseDayCanvasDispatcherArgs): UseDayCanvasDispatcherResult {
  const { refresh, setToast } = args;
  const reassignEntry = useReassignScheduleEntry();
  const assignWalkIn = useAssignWalkIn();
  const [serverConflicts, setServerConflicts] = useState<ConflictDetail[] | null>(null);
  const [pendingReassign, setPendingReassign] = useState<PendingReassign | null>(null);
  const [pendingWalkInAssign, setPendingWalkInAssign] = useState<PendingWalkInAssign | null>(null);

  const conflictRpcName: 'assign_walk_in' | 'reassign_schedule_entry' = pendingWalkInAssign
    ? 'assign_walk_in'
    : 'reassign_schedule_entry';

  const handleReassign = useCallback(async (droppedEntryId: string, newResourceId: string) => {
    setServerConflicts(null);
    const result = await reassignEntry({ entryId: droppedEntryId, newAssigneeId: newResourceId, force: false });
    if (!result.success) {
      if (result.error.code === 'CONFLICT_DETECTED' && result.error.conflicts) {
        setServerConflicts(result.error.conflicts);
        setPendingReassign({ entryId: droppedEntryId, newAssigneeId: newResourceId });
        setPendingWalkInAssign(null);
      } else {
        setToast({ msg: lookupErrorCopy('reassign_schedule_entry', result.error.code), type: 'error' });
      }
      return;
    }
    refresh();
    setToast({ msg: 'Reassigned', type: 'success' });
  }, [reassignEntry, refresh, setToast]);

  const handleWalkInDrop = useCallback(async (walkInId: string, operatorId: string, anchorDate: Date) => {
    setServerConflicts(null);
    const now = new Date();
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    const snappedMinutes = Math.ceil(minutesNow / 15) * 15;
    const hh = String(Math.floor(snappedMinutes / 60) % 24).padStart(2, '0');
    const mm = String(snappedMinutes % 60).padStart(2, '0');
    const startTime = `${hh}:${mm}:00`;
    const yyyy = anchorDate.getFullYear();
    const mo = String(anchorDate.getMonth() + 1).padStart(2, '0');
    const dd = String(anchorDate.getDate()).padStart(2, '0');
    const startDate = `${yyyy}-${mo}-${dd}`;

    const result = await assignWalkIn({ walkInId, operatorId, startDate, startTime, force: false });
    if (!result.success) {
      if (result.error.code === 'CONFLICT_DETECTED' && result.error.conflicts) {
        setServerConflicts(result.error.conflicts);
        setPendingWalkInAssign({ walkInId, operatorId, startDate, startTime });
        setPendingReassign(null);
      } else {
        setToast({ msg: lookupErrorCopy('assign_walk_in', result.error.code), type: 'error' });
      }
      return;
    }
    refresh();
    setToast({ msg: 'Walk-in assigned', type: 'success' });
  }, [assignWalkIn, refresh, setToast]);

  const handleOverride = useCallback(async () => {
    if (pendingWalkInAssign) {
      const result = await assignWalkIn({ ...pendingWalkInAssign, force: true });
      setServerConflicts(null);
      setPendingWalkInAssign(null);
      if (!result.success) {
        setToast({ msg: lookupErrorCopy('assign_walk_in', result.error.code), type: 'error' });
        return;
      }
      refresh();
      setToast({ msg: 'Walk-in assigned (override)', type: 'success' });
      return;
    }
    if (!pendingReassign) return;
    const result = await reassignEntry({ entryId: pendingReassign.entryId, newAssigneeId: pendingReassign.newAssigneeId, force: true });
    setServerConflicts(null);
    setPendingReassign(null);
    if (!result.success) {
      setToast({ msg: lookupErrorCopy('reassign_schedule_entry', result.error.code), type: 'error' });
      return;
    }
    refresh();
    setToast({ msg: 'Reassigned (override)', type: 'success' });
  }, [pendingReassign, pendingWalkInAssign, reassignEntry, assignWalkIn, refresh, setToast]);

  const handleDismiss = useCallback(() => {
    setServerConflicts(null);
    setPendingReassign(null);
    setPendingWalkInAssign(null);
  }, []);

  return {
    serverConflicts,
    conflictRpcName,
    handleReassign,
    handleWalkInDrop,
    handleOverride,
    handleDismiss,
  };
}
